import { useMemo, useState, useCallback } from "react";
import { Badge } from "./badge";
import { Tooltip, TooltipTrigger, TooltipContent } from "./tooltip";
import { Popover, PopoverTrigger } from "./popover";
import { Tag, Plus } from "lucide-react";
import { cn } from "../lib/utils";
import { Trans, useTranslation } from "react-i18next";
import { StringSelectionPopover } from "./StringSelectionPopover";
import type { StringSelectionPopoverOption } from "./StringSelectionPopover";

export interface MessageTagSelectorProps {
  availableTags?: string[];
  selectedTags: string[];
  onTagsChange?: (selectedTags: string[]) => void;
  onTagSelect?: (toggledTag: string) => void;
  className?: string;
  maxVisible?: number;
  placeholder?: string;
}

export const MessageTagSelector = ({
  availableTags,
  selectedTags = [],
  onTagsChange,
  onTagSelect,
  className,
  maxVisible = 2,
  placeholder,
}: MessageTagSelectorProps) => {
  const { t } = useTranslation("tags");
  const [isOpen, setIsOpen] = useState(false);

  const visibleTags = selectedTags.slice(0, maxVisible);
  const remainingCount = Math.max(0, selectedTags.length - maxVisible);

  const allTags = useMemo(
    () => Array.from(new Set<string>([...(availableTags ?? []), ...selectedTags])),
    [availableTags, selectedTags]
  );

  const options: StringSelectionPopoverOption[] = useMemo(
    () => allTags.map((tag) => ({ label: tag, value: tag })),
    [allTags]
  );

  const handleToggle = useCallback(
    (tagName: string) => {
      const isCurrentlySelected = selectedTags.includes(tagName);
      if (isCurrentlySelected) {
        onTagsChange?.(selectedTags.filter((name) => name !== tagName));
      } else {
        onTagsChange?.([...selectedTags, tagName]);
      }
      onTagSelect?.(tagName);
    },
    [selectedTags, onTagsChange, onTagSelect]
  );

  const handleCreate = useCallback(
    (value: string) => {
      if (!selectedTags.includes(value)) {
        onTagsChange?.([...selectedTags, value]);
        onTagSelect?.(value);
      }
    },
    [selectedTags, onTagsChange, onTagSelect]
  );

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div className={cn("relative flex items-center gap-1 cursor-pointer min-h-[32px]", className)}>
          <div className="flex items-center gap-1">
            {selectedTags?.length === 0 ? (
              <div className="w-6 h-6 hover:bg-muted transition-colors rounded-md flex items-center justify-center">
                <Tag className="w-3 h-3 text-muted-foreground" />
              </div>
            ) : (
              <>
                {visibleTags.map((tag) => (
                  <Tooltip key={tag}>
                    <TooltipTrigger asChild>
                      <Badge variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{tag}</p>
                    </TooltipContent>
                  </Tooltip>
                ))}
                {remainingCount > 0 && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge variant="outline" className="text-xs">
                        +{remainingCount}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{t("{{count}} more tag", { count: remainingCount })}</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </>
            )}
          </div>
        </div>
      </PopoverTrigger>
      <StringSelectionPopover
        options={options}
        selectedValues={selectedTags}
        onToggle={handleToggle}
        searchPlaceholder={placeholder || t("Search or add tags...")}
        className="w-80"
        emptyText={t("No tags available")}
        creator={{
          render: (inputValue) => (
            <div className="flex items-center gap-2">
              <Plus className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">
                <Trans t={t}>
                  Add "<span className="font-semibold">{inputValue}</span>"
                </Trans>
              </span>
            </div>
          ),
          isValidNewOption: (inputValue) => {
            const trimmed = inputValue.trim();
            return trimmed.length > 0 && !allTags.some((tag) => tag.toLowerCase() === trimmed.toLowerCase());
          },
          onCreate: handleCreate,
        }}
      />
    </Popover>
  );
};
